import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right';
import SEOHead from '../components/SEOHead';
import SITE_META from '../components/public/siteMeta.json';
import PublicShell, { APP_STORE_URL, PLAY_STORE_URL } from '../components/public/PublicShell';
import StoreButtons, { StoreButton } from '../components/public/StoreButton';
import PartnerWall from '../components/public/PartnerWall';
import Timeline from '../components/public/Timeline';
import { detectPlatform, openStore, STORES } from '../components/public/platform';
import { Corners, CURRENCY_MARKS, LiveEyebrow, Reveal, SplitLines, useIdleScript } from '../components/public/primitives';
import { BODY, EYEBROW, H1, H2, INTRO, LINK_QUIET, MONO, SECTION, WRAP } from '../components/public/register';

// Title, description and share card come from siteMeta.json, which the build also stamps
// into the static HTML that link unfurlers read.
const META = SITE_META.pages['/download'];

/* ---------------------------------------------------------------------------
   yunlen.app/download — mobile first. Most visitors arrive on a phone from an
   ad or a shared link, so the first screen is the app, one button for their
   store and a countdown they can stop. Desktops get both badges and a QR code
   that opens this page on a phone. Below that, two lists — no tiles.

   The platform is read in the state initialiser, so the first paint already
   shows the right store instead of both and then one.
   --------------------------------------------------------------------------- */

const COUNTDOWN_FROM = 5;

function normalizeDownloadPath(pathname) {
  let normalized = pathname
    .replace(/%E2%80%AC/g, '')
    .replace(/%E2%80%8B/g, '')
    .replace(/%E2%80%8C/g, '')
    .replace(/%E2%80%8D/g, '')
    .replace(/[​-‍﻿]/g, '');

  if (normalized.toLowerCase().startsWith('/download')) {
    normalized = '/download';
  }

  return normalized;
}

const STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Yunlen',
  description: 'Phone app for virtual cards, USD, EUR, GBP, and CAD accounts, wires, ACH, SEPA, and stablecoin wallets.',
  url: SITE_META.origin,
  applicationCategory: 'FinanceApplication',
  operatingSystem: ['Android', 'iOS'],
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '1250',
  },
  downloadUrl: [PLAY_STORE_URL, APP_STORE_URL],
  screenshot: '/ic_launcher.png',
  featureList: [
    'Virtual cards',
    'USD, EUR, GBP, and CAD accounts',
    'Wires, ACH, and SEPA',
    'Stablecoin wallets',
  ],
  author: {
    '@type': 'Organization',
    name: 'Yunlen',
    url: SITE_META.origin,
  },
};

const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js?id=AW-17555435567';
const GTAG_INIT = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'AW-17555435567');`;

const HERO_LINES = ['Get Yunlen', 'on your phone.'];

const FACTS = [
  { title: 'Virtual cards', body: 'Visa and Mastercard virtual cards for online payments.' },
  { title: 'Accounts', body: 'USD, EUR, GBP, and CAD accounts. US and EU accounts include an IBAN.' },
  { title: 'Transfers', body: 'Wires, ACH, and SEPA to more than 200 countries.' },
  { title: 'Wallets', body: 'Stablecoin wallets for holding and sending USDT and USDC.' },
];

const STEPS = [
  { title: 'Install', desc: 'From the App Store or Google Play. The app is free to download.' },
  { title: 'Apply', desc: 'Create your account and verify your identity, all in the app.' },
  { title: 'Use it', desc: 'Account approval and card activation finish within minutes.' },
];

/* ---------- App identity ----------
   The store-listing row a phone visitor recognises: icon, name, what it is. */

const APP_ROW = (
  <div className="flex items-center gap-3.5">
    <img
      src="/ic_launcher.png"
      alt=""
      width={64}
      height={64}
      className="h-16 w-16 rounded-[1.1rem] shadow-[0_14px_28px_-16px_rgba(15,23,20,0.6)]"
    />
    <div className="flex flex-col gap-1">
      <span className="text-[1.125rem] font-medium leading-none tracking-[-0.02em] text-[#171717]">Yunlen</span>
      <span className={`${MONO} text-[0.625rem] uppercase tracking-[0.1em] text-[#565656]`}>Finance · Free</span>
    </div>
  </div>
);

/* ---------- The feature line (phones) ----------
   One sentence, set large and full width right above the button: the four
   things in dark ink against the muted connecting words, with the actual
   flags and token logos inline. No underlines or highlights. */

const MARKS = (codes) => (
  <span className="mr-1 inline-flex -space-x-1 align-[-0.2em]" aria-hidden="true">
    {codes.map((c) => (
      <img key={c} src={CURRENCY_MARKS[c]} alt="" width={20} height={20} className="h-5 w-5 rounded-full ring-[1.5px] ring-[#ffffff]" />
    ))}
  </span>
);

const HL = ({ children }) => <strong className="font-semibold text-[#171717]">{children}</strong>;

const FEATURE_SENTENCE = (
  <p className="text-[clamp(1.25rem,5.6vw,1.5rem)] leading-[1.42] tracking-[-0.02em] text-[#6b6b6b]">
    <HL>Virtual cards</HL>, {MARKS(['USD', 'EUR', 'GBP', 'CAD'])}
    <HL>USD, EUR, GBP and CAD accounts</HL>, <HL>wires, ACH, SEPA</HL>, and {MARKS(['USDT', 'USDC'])}
    <HL>wallets for USDT and USDC</HL>.
  </p>
);

/* ---------- Countdown (phones) ----------
   One wide button for this phone's store, a thin progress line, and a way
   to stay. No second store: a phone only ever needs its own. */

function PhoneAction({ platform }) {
  const [left, setLeft] = useState(COUNTDOWN_FROM);
  const [cancelled, setCancelled] = useState(false);
  const store = STORES[platform];

  // One timeout per second, derived from state — no side effect inside a
  // setState updater, which StrictMode would run twice.
  useEffect(() => {
    if (cancelled) return undefined;
    if (left === 0) {
      openStore(platform);
      return undefined;
    }
    const id = window.setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [left, cancelled, platform]);

  const counting = !cancelled && left > 0;
  const progress = cancelled ? 0 : (COUNTDOWN_FROM - left) / COUNTDOWN_FROM;

  return (
    <div className="w-full">
      <StoreButton platform={platform} onClick={() => openStore(platform)} block />
      <div className="mt-5 h-[3px] w-full max-w-[26rem] overflow-hidden rounded-full bg-[#ececec]" aria-hidden="true">
        <span
          className="block h-full origin-left rounded-full bg-[#10b981] transition-transform duration-1000 ease-linear motion-reduce:transition-none"
          style={{ transform: `scaleX(${progress})` }}
        />
      </div>
      <div className="mt-2 flex min-h-[2.75rem] w-full max-w-[26rem] items-center justify-between gap-4">
        <span className={`${MONO} text-[0.6875rem] uppercase tracking-[0.1em] text-[#565656]`} aria-live="polite">
          {cancelled ? 'Redirect stopped' : counting ? `Opening in ${left}s` : `Opening ${store.name}`}
        </span>
        {counting ? (
          <button type="button" onClick={() => setCancelled(true)} className={`${LINK_QUIET} min-h-[2.75rem] px-1`}>
            Stay here
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ---------- QR (desktops) ----------
   The encoder is imported on demand: phones, which never render this panel,
   never download it. */

function QrPanel() {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let live = true;
    const target = `${window.location.origin}/download`;
    import('qrcode')
      .then((mod) =>
        (mod.default || mod).toDataURL(target, {
          margin: 0,
          width: 440,
          errorCorrectionLevel: 'M',
          color: { dark: '#171717', light: '#ffffff' },
        })
      )
      .then((url) => {
        if (live) setSrc(url);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="relative mx-auto hidden w-full max-w-[22rem] md:block">
      <Corners flash />
      <div className="flex flex-col items-center gap-6 rounded-[2rem] bg-[#f5f5f5] p-9 text-center lg:rounded-[3rem] lg:p-10">
        <span className={`${EYEBROW} text-[#565656]`}>Scan with your phone</span>
        <div className="grid aspect-square w-full max-w-[13rem] place-items-center rounded-[1.5rem] bg-[#ffffff] p-5 shadow-[0_24px_48px_-32px_rgba(0,0,0,0.4)]">
          {src ? (
            <img src={src} alt="QR code that opens the Yunlen download page" width={208} height={208} className="h-full w-full [image-rendering:pixelated]" />
          ) : (
            <span className="h-full w-full animate-pulse rounded-xl bg-[#f1f1f1]" aria-hidden="true" />
          )}
        </div>
        <p className={`${BODY} max-w-[16rem]`}>Point your camera at the code. It opens the right store for your phone.</p>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */

const DownloadPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [platform] = useState(detectPlatform);

  useIdleScript(GTAG_SRC, GTAG_INIT);

  useEffect(() => {
    const currentPath = location.pathname;
    if (!currentPath.toLowerCase().startsWith('/download')) return;
    const normalizedPath = normalizeDownloadPath(currentPath);
    if (normalizedPath !== currentPath && normalizedPath === '/download') {
      navigate(normalizedPath + location.search, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const cta = platform ? { label: `Open ${STORES[platform].name}`, onClick: () => openStore(platform) } : null;

  return (
    <>
      <SEOHead
        title={META.title}
        description={META.description}
        keywords={META.keywords}
        canonicalUrl={`${SITE_META.origin}/download`}
        ogImage={`${SITE_META.origin}${META.ogImage}`}
        ogImageWidth={1200}
        ogImageHeight={630}
        structuredData={STRUCTURED_DATA}
      />
      <PublicShell cta={cta}>
        {/* ============================== HERO ============================== */}
        <section className="relative overflow-clip pb-16 pt-8 sm:pb-24 sm:pt-14 lg:pb-28 lg:pt-20">
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[36rem] overflow-hidden">
            <span className="yl-wash left-[-24%] top-[-10rem] h-[24rem] w-[30rem] bg-[#cfeee0] lg:left-[-4%] lg:w-[36rem]" />
            <span className="yl-wash yl-wash--b right-[-24%] top-[4rem] h-[18rem] w-[22rem] bg-[#9fd9bf]/50 lg:right-[10%] lg:w-[26rem]" />
            <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-b from-transparent to-[#ffffff]" />
          </div>

          <div className={`${WRAP} grid items-center gap-12 md:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:gap-16`}>
            <div className="flex flex-col items-start">
              {platform ? APP_ROW : <LiveEyebrow>iPhone and Android</LiveEyebrow>}

              {/* Bigger and bolder on a phone, where the headline is the whole first screen. */}
              <h1
                aria-label={HERO_LINES.join(' ')}
                className={`${H1} mt-6 !text-[clamp(2.875rem,13vw,3.5rem)] !font-bold !leading-[0.98] !tracking-[-0.045em] sm:mt-8 sm:!text-[3.75rem] sm:!font-medium sm:!leading-[1.03] sm:!tracking-[-0.03em] lg:!text-[4.75rem]`}
              >
                <SplitLines lines={HERO_LINES} delay={0.1} accentLast className="block" />
              </h1>

              {platform ? (
                <>
                  <div className="mt-6 w-full">{FEATURE_SENTENCE}</div>
                  <div className="mt-10 w-full">
                    <PhoneAction platform={platform} />
                  </div>
                </>
              ) : (
                <>
                  <p className={`${INTRO} mt-6 max-w-[32rem]`}>
                    Virtual cards, USD, EUR, GBP and CAD accounts, wires, ACH, SEPA, and wallets for USDT and USDC.
                  </p>
                  <StoreButtons className="mt-8" />
                </>
              )}
            </div>

            {platform ? null : <QrPanel />}
          </div>
        </section>

        <div className="pb-16 sm:pb-24">
          <div className={WRAP}>
            <p className={`${EYEBROW} mb-7 text-[#565656] md:text-center`}>Our partners</p>
            <PartnerWall />
          </div>
        </div>

        {/* ========================== WHAT YOU GET ==========================
            One list with hairlines; two columns from tablet up. */}
        <section className={`${SECTION} yl-defer pt-0 sm:pt-0 lg:pt-0`}>
          <div className={WRAP}>
            <Reveal className="flex flex-col gap-4 sm:gap-6">
              <span className={`${EYEBROW} text-[#565656]`}>In the app</span>
              <h2 className={`${H2} max-w-[14ch]`}>What you get.</h2>
            </Reveal>
            <Reveal as="ul" className="mt-8 grid border-t border-[#ececec] sm:grid-cols-2 sm:gap-x-12 lg:mt-14 lg:gap-x-16" stagger={0.05} y={14}>
              {FACTS.map((fact) => (
                <li key={fact.title} className="flex gap-4 border-b border-[#ececec] py-6 sm:py-8">
                  <span className="mt-[0.55rem] h-2 w-2 shrink-0 rounded-full bg-[#10b981]" aria-hidden="true" />
                  <div>
                    <h3 className="text-[1.25rem] font-medium leading-tight tracking-[-0.02em] text-[#171717] lg:text-[1.5rem]">{fact.title}</h3>
                    <p className={`${BODY} mt-1.5 max-w-[40ch]`}>{fact.body}</p>
                  </div>
                </li>
              ))}
            </Reveal>
          </div>
        </section>

        {/* ============================== STEPS ============================== */}
        <section className={`${SECTION} yl-defer pt-0 sm:pt-0 lg:pt-0`}>
          <div className={WRAP}>
            <Reveal className="flex flex-col gap-4 sm:gap-6">
              <span className={`${EYEBROW} text-[#565656]`}>Three steps</span>
              <h2 className={`${H2} max-w-[14ch]`}>Up and running in minutes.</h2>
            </Reveal>
            <Reveal className="mt-10 lg:mt-16" y={16}>
              <Timeline steps={STEPS} />
            </Reveal>

            <Link to="/" className="group mt-20 flex items-center justify-between gap-5 border-y border-[#ececec] py-7 sm:py-9 lg:mt-28">
              <span className="flex min-w-0 flex-col gap-2">
                <span className={`${EYEBROW} text-[#565656]`}>Before you install</span>
                <span className="text-[1.25rem] font-medium leading-snug tracking-[-0.02em] text-[#171717] sm:text-[1.75rem] lg:text-[2.25rem]">
                  See what Yunlen does.
                </span>
              </span>
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#f5f5f5] text-[#171717] transition-colors duration-300 group-hover:bg-[#171717] group-hover:text-[#ffffff]" aria-hidden="true">
                <ArrowUpRight className="h-5 w-5" />
              </span>
            </Link>
          </div>
        </section>
      </PublicShell>
    </>
  );
};

export default DownloadPage;
